/**
 * The backend's reference lists — Title, Goal, Life style and the rest — for
 * whichever form is asking.
 *
 * A form names the lists it needs and gets one entry back per list, each with
 * its own status: they are separate calls, and a goals endpoint that is down
 * is no reason to hold the Title field shut. The Add client form takes the
 * six it has fields for (`useClientLookups`); the Add enquiry form takes
 * three (`useEnquiryLookups`). No form takes every list here - the goals and
 * the services are two answers to the same question, and each form asks for
 * the one its endpoint takes.
 *
 * The lists are not cached between mounts. They are small, they change from
 * under us whenever the backend's are edited, and these forms are opened
 * rarely enough that a request per visit is the cheaper trade than a stale
 * list.
 *
 * Must be used inside a Clerk provider and an active session: every endpoint
 * is 401 without a bearer token.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import {
  fetchAllergens,
  fetchCuisines,
  fetchDietaryPatterns,
  fetchGoals,
  fetchLifeStyles,
  fetchMedicalConditions,
  fetchServiceGoals,
  fetchTitles,
} from "../services/catalogService";
import type { GetToken } from "../../auth/types/auth.types";
import type { LookupOption } from "../types/clients.types";

/**
 * Loading, ready, or the request came back with nothing usable. Tracked per
 * list rather than for the set — see the note above.
 */
export type LookupStatus = "loading" | "ready" | "failed";

export interface CatalogLookup {
  /** Rows as the API sent them, id included — a saved record carries the id. */
  options: LookupOption[];
  /** Just the names, which is what the dropdown renders. */
  names: string[];
  status: LookupStatus;
}

/**
 * Every list a form can ask for, and the getter behind each. One entry per
 * dropdown — adding a list here is all it takes to have it loaded, reported
 * and retried alongside the rest.
 */
const CATALOGS = {
  titles: fetchTitles,
  /** The backend's goal catalog, which the Add enquiry form picks from. */
  goals: fetchGoals,
  /**
   * The practitioner's own active services, which is what the Add and Edit
   * client forms' Goal field picks from. A separate entry rather than a
   * replacement for `goals` above: the two forms send different things - a
   * client carries service ids, an enquiry carries catalog goal ids - and one
   * list serving both would put the wrong ids on one of the wires.
   */
  serviceGoals: fetchServiceGoals,
  dietaryPatterns: fetchDietaryPatterns,
  allergens: fetchAllergens,
  cuisines: fetchCuisines,
  lifeStyles: fetchLifeStyles,
  medicalConditions: fetchMedicalConditions,
} satisfies Record<
  string,
  (getToken: GetToken, signal?: AbortSignal) => Promise<LookupOption[]>
>;

export type CatalogName = keyof typeof CATALOGS;

export type CatalogLookups<N extends CatalogName> = Record<N, CatalogLookup> & {
  /**
   * Runs every list call again.
   *
   * Only ever called from something the user pressed on purpose — a form's
   * Try again button. Nothing about opening, closing or reaching for a
   * dropdown may call it: the lists are fetched once when the page opens, and
   * a control that refetched on every interaction would put a request per list
   * on the wire each time someone changed their mind about a field.
   */
  reload: () => void;
};

/** What the hook holds per list, before the display names are derived. */
interface LookupState {
  options: LookupOption[];
  status: LookupStatus;
}

const EMPTY: LookupOption[] = [];

/** A list that has not been heard from at all — what a fresh mount reads as. */
const PENDING: LookupState = { options: EMPTY, status: "loading" };

const failureMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

export function useCatalogLookups<N extends CatalogName>(
  names: readonly N[]
): CatalogLookups<N> {
  const { getToken } = useAuth();
  const toast = useToast();

  // Clerk hands back a fresh `getToken` on some renders, and the load below
  // must not re-run for that — a fetch per render is a loop, and a form
  // re-renders on every keystroke. The effect reads the current one through
  // this rather than depending on its identity.
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  // Which lists to fetch, read the same way and for the same reason: the
  // effect is keyed on the names themselves rather than on the array holding
  // them, so a caller that writes the list inline still fetches once.
  const namesRef = useRef(names);
  namesRef.current = names;
  const wanted = names.join(",");

  const [lookups, setLookups] = useState<Partial<Record<CatalogName, LookupState>>>(
    {}
  );
  // Bumped by `reload`, which is the whole of what re-runs the effect.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const asked = namesRef.current;

    // A retry keeps whatever already loaded on screen while it runs: only the
    // status goes back to loading, so a working field does not blink empty.
    setLookups((current) =>
      Object.fromEntries(
        asked.map((name) => [
          name,
          { options: current[name]?.options ?? EMPTY, status: "loading" },
        ])
      ) as Partial<Record<CatalogName, LookupState>>
    );

    const getCurrentToken = () => getTokenRef.current();

    // `allSettled`, not `all`: one endpoint failing must not throw away the
    // lists the others returned.
    void Promise.allSettled(
      asked.map((name) => CATALOGS[name](getCurrentToken, controller.signal))
    ).then((results) => {
      // Unmounted, or a retry has already replaced this run.
      if (controller.signal.aborted) return;

      const next: Partial<Record<CatalogName, LookupState>> = {};
      let failure: unknown;

      asked.forEach((name, index) => {
        const result = results[index];

        if (result.status === "fulfilled") {
          next[name] = { options: result.value, status: "ready" };
          return;
        }

        next[name] = { options: EMPTY, status: "failed" };
        console.error(`${name} failed to load:`, result.reason);
        // The first failure is the one reported: they go down together, for
        // the same reason, and several identical toasts say nothing extra.
        if (failure === undefined) failure = result.reason;
      });

      setLookups(next);

      if (failure !== undefined) {
        toast.error("Could not load the form's options", failureMessage(failure));
      }
    });

    return () => controller.abort();
  }, [toast, attempt, wanted]);

  const reload = useCallback(() => setAttempt((current) => current + 1), []);

  return useMemo(() => {
    const resolved = Object.fromEntries(
      names.map((name) => {
        const state = lookups[name] ?? PENDING;
        return [
          name,
          {
            options: state.options,
            names: state.options.map((option) => option.name),
            status: state.status,
          },
        ];
      })
    ) as Record<N, CatalogLookup>;

    return { ...resolved, reload };
  }, [lookups, names, reload]);
}
